import { IHeroSection } from 'src/app/services/hero-section.service';

export const HERO_SLIDE_INTERVAL_MS = 9000;
export const HERO_CROSSFADE_ANIMATION = 'heroCrossfade';
const HERO_CROSSFADE_FALLBACK_MS = 1300;

export class HeroSliderController {
  heroSections: IHeroSection[] = [];
  currentIndex = 0;
  prevIndex = 0;
  isFading = false;

  private slideTimer: ReturnType<typeof setInterval> | null = null;
  private fadeFallbackTimer: ReturnType<typeof setTimeout> | null = null;

  get currentSection(): IHeroSection | null {
    return this.heroSections[this.currentIndex] ?? null;
  }

  get prevSection(): IHeroSection | null {
    return this.heroSections[this.prevIndex] ?? null;
  }

  setSections(sections: IHeroSection[]): void {
    this.stopSlider();
    this.clearFadeFallback();
    this.heroSections = sections;
    this.currentIndex = 0;
    this.prevIndex = 0;
    this.isFading = false;

    if (sections.length <= 1) {
      return;
    }

    void this.preloadImages(sections).then(() => {
      if (this.heroSections === sections && sections.length > 1) {
        this.startSlider();
      }
    });
  }

  destroy(): void {
    this.stopSlider();
    this.clearFadeFallback();
  }

  onFadeAnimationEnd(animationName: string): void {
    if (!this.isFading) {
      return;
    }

    if (!animationName.includes(HERO_CROSSFADE_ANIMATION)) {
      return;
    }

    this.completeFade();
  }

  private completeFade(): void {
    this.clearFadeFallback();
    this.isFading = false;
    this.prevIndex = this.currentIndex;
  }

  private nextSlide(): void {
    if (this.heroSections.length <= 1 || this.isFading) {
      return;
    }

    this.isFading = true;
    this.prevIndex = this.currentIndex;
    this.currentIndex = (this.currentIndex + 1) % this.heroSections.length;
    this.scheduleFadeFallback();
  }

  private scheduleFadeFallback(): void {
    this.clearFadeFallback();
    this.fadeFallbackTimer = setTimeout(() => {
      if (this.isFading) {
        this.completeFade();
      }
    }, HERO_CROSSFADE_FALLBACK_MS);
  }

  private clearFadeFallback(): void {
    if (this.fadeFallbackTimer !== null) {
      clearTimeout(this.fadeFallbackTimer);
      this.fadeFallbackTimer = null;
    }
  }

  private startSlider(): void {
    this.stopSlider();
    this.slideTimer = setInterval(
      () => this.nextSlide(),
      HERO_SLIDE_INTERVAL_MS,
    );
  }

  private stopSlider(): void {
    if (this.slideTimer !== null) {
      clearInterval(this.slideTimer);
      this.slideTimer = null;
    }
  }

  private preloadImages(sections: IHeroSection[]): Promise<void> {
    const loads = sections.map((section) => {
      const url = section.imageUrl?.trim();
      if (!url) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = url;
      });
    });

    return Promise.all(loads).then(() => undefined);
  }
}
